import React, {
  useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { trackEvent } from '../../utils/analytics';
import { formatDuration } from './podcastShow';

/**
 * PodcastPlayer — the custom on-site audio player (CEL-1355, PRD R7-R10).
 *
 * Analytics-safe by contract (R8 — Transistor's IAB download stats depend on
 * this): preload="none", NO autoplay, NO crossorigin, NO cache-busting params,
 * NO prefetching of other episodes. The browser's native Range requests do the
 * streaming; nothing loads until the listener presses play.
 *
 * Controls (R9): play/pause, seek (with buffered indicator), elapsed/total,
 * speed cycle 1→1.25→1.5→2×, chapter menu. Deep links: a startAt prop (parsed
 * from ?t= by the page) seeks on FIRST play — respecting preload=none, no
 * metadata fetch happens before listener intent. Keyboard: space toggles,
 * ←/→ skip 15s when the player has focus.
 *
 * apiRef exposes { seekTo(seconds, { andPlay }) } for transcript-row and
 * chapter-list seeking from the page.
 *
 * Quartile analytics: podcast_play_progress fired once per quartile
 * (25/50/75/100) per page view, computed against the known durationSeconds.
 */
const SPEEDS = [1, 1.25, 1.5, 2];

const PodcastPlayer = ({
  src,
  durationSeconds,
  chapters = [],
  slug,
  startAt = 0,
  apiRef,
  onTimeUpdate,
  variant = 'bar', // 'bar' (hub featured entry) | 'spine' (episode-page rail)
}) => {
  const audioRef = useRef(null);
  const rootRef = useRef(null);
  const firedQuartiles = useRef({});
  const pendingStart = useRef(startAt > 0 ? Math.min(startAt, durationSeconds - 1) : 0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const chapWrapRef = useRef(null);

  const duration = durationSeconds || 0;

  // Chapters menu dismissal: click anywhere outside it (or Escape) closes it —
  // standard dropdown behavior (Nitish, shift 102: "the only way to close it
  // was clicking Chapters again"). mousedown (not click) so the menu is gone
  // before the outside element handles its own click.
  useEffect(() => {
    if (!chaptersOpen) return undefined;
    const onDocDown = (ev) => {
      if (chapWrapRef.current && !chapWrapRef.current.contains(ev.target)) {
        setChaptersOpen(false);
      }
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setChaptersOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [chaptersOpen]);

  const play = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (pendingStart.current > 0) {
      a.currentTime = pendingStart.current;
      pendingStart.current = 0;
    }
    a.play().catch(() => {});
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) play();
    else a.pause();
  }, [play]);

  const seekTo = useCallback((t, { andPlay = false } = {}) => {
    const a = audioRef.current;
    if (!a) return;
    pendingStart.current = 0;
    a.currentTime = Math.max(0, Math.min(t, duration - 0.25));
    setCurrentTime(a.currentTime);
    if (andPlay && a.paused) a.play().catch(() => {});
  }, [duration]);

  useImperativeHandle(apiRef, () => ({ seekTo }), [seekTo]);

  // Media element listeners.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onPlay = () => {
      setPlaying(true);
      if (!firedQuartiles.current.start) {
        firedQuartiles.current.start = true;
        trackEvent('podcast_play_start', { episode: slug });
      }
    };
    const onPause = () => setPlaying(false);
    const onTime = () => {
      setCurrentTime(a.currentTime);
      if (onTimeUpdate) onTimeUpdate(a.currentTime);
      if (duration > 0) {
        const pct = (a.currentTime / duration) * 100;
        [25, 50, 75].forEach((q) => {
          if (pct >= q && !firedQuartiles.current[q]) {
            firedQuartiles.current[q] = true;
            trackEvent('podcast_play_progress', { episode: slug, quartile: q });
          }
        });
      }
      try {
        if (a.buffered.length > 0) setBuffered(a.buffered.end(a.buffered.length - 1));
      } catch (e) { /* buffered ranges can throw during seeks — cosmetic only */ }
    };
    const onEnded = () => {
      setPlaying(false);
      if (!firedQuartiles.current[100]) {
        firedQuartiles.current[100] = true;
        trackEvent('podcast_play_progress', { episode: slug, quartile: 100 });
      }
    };
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnded);
    };
  }, [slug, duration, onTimeUpdate]);

  // Keyboard: space toggles, arrows skip 15s — only when focus is inside the player.
  const onKeyDown = (ev) => {
    if (ev.target.tagName === 'INPUT' && ev.target.type !== 'range') return;
    if (ev.key === ' ' || ev.key === 'Spacebar') {
      ev.preventDefault();
      toggle();
    } else if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      seekTo(currentTime - 15);
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      seekTo(currentTime + 15);
    }
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    const a = audioRef.current;
    if (a) a.playbackRate = SPEEDS[next];
  };

  const onSeekInput = (ev) => {
    seekTo(parseFloat(ev.target.value));
  };

  const jumpChapter = (c) => {
    setChaptersOpen(false);
    seekTo(c.startTime, { andPlay: true });
    trackEvent('podcast_chapter_click', { episode: slug, chapter: c.title });
  };

  const copyTimestampLink = () => {
    const t = Math.floor(currentTime);
    const url = `${window.location.origin}/podcast/${slug}/?t=${t}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }).catch(() => {});
    }
    trackEvent('podcast_copy_timestamp', { episode: slug, t });
  };

  const activeChapter = chapters.reduce(
    (acc, c) => (currentTime >= c.startTime ? c : acc),
    chapters[0] || null,
  );

  if (variant === 'spine') {
    return (
      <div
        className="pod-sp"
        ref={rootRef}
        onKeyDown={onKeyDown}
        role="group"
        aria-label="Podcast player"
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- audio-only
            player; the full speaker-labeled transcript is on-page (R5). */}
        <audio ref={audioRef} src={src} preload="none" />
        <div className="pod-sp__ctrl">
          <button
            type="button"
            className="pod-sp__btn"
            onClick={toggle}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86a1 1 0 0 0-1.5.86z" />
              </svg>
            )}
          </button>
          <span className="pod-sp__time">
            {formatDuration(currentTime)} / {formatDuration(duration)}
          </span>
          <div className="pod-sp__chips">
            <button
              type="button"
              className="pod-sp__chip"
              onClick={cycleSpeed}
              aria-label={`Playback speed ${SPEEDS[speedIdx]}x`}
            >
              {SPEEDS[speedIdx]}×
            </button>
            <button
              type="button"
              className="pod-sp__chip"
              onClick={copyTimestampLink}
              aria-label="Copy link at current time"
            >
              {copied ? 'Copied' : 'Share'}
            </button>
          </div>
        </div>
        <div className="pod-sp__seekwrap pod-player__seekwrap">
          <div
            className="pod-player__buffered"
            style={{ width: duration ? `${Math.min(100, (buffered / duration) * 100)}%` : 0 }}
            aria-hidden="true"
          />
          <div
            className="pod-player__progress"
            style={{ width: duration ? `${Math.min(100, (currentTime / duration) * 100)}%` : 0 }}
            aria-hidden="true"
          />
          <input
            type="range"
            className="pod-player__seek"
            min={0}
            max={duration}
            step={1}
            value={currentTime}
            onChange={onSeekInput}
            aria-label="Seek"
          />
        </div>
        {pendingStart.current > 0 && !playing && currentTime === 0 && (
          <div className="pod-sp__startat">
            Starts at {formatDuration(pendingStart.current)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="pod-player"
      ref={rootRef}
      onKeyDown={onKeyDown}
      role="group"
      aria-label="Podcast player"
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- audio-only
          player; the full speaker-labeled transcript is on-page (R5). */}
      <audio ref={audioRef} src={src} preload="none" />

      {pendingStart.current > 0 && !playing && currentTime === 0 && (
        <div className="pod-player__startat">
          Starts at {formatDuration(pendingStart.current)}
        </div>
      )}

      <div className="pod-player__row">
        <button
          type="button"
          className="pod-player__play"
          onClick={toggle}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86a1 1 0 0 0-1.5.86z" />
            </svg>
          )}
        </button>

        <span className="pod-player__time">{formatDuration(currentTime)}</span>

        <div className="pod-player__seekwrap">
          <div
            className="pod-player__buffered"
            style={{ width: duration ? `${Math.min(100, (buffered / duration) * 100)}%` : 0 }}
            aria-hidden="true"
          />
          <div
            className="pod-player__progress"
            style={{ width: duration ? `${Math.min(100, (currentTime / duration) * 100)}%` : 0 }}
            aria-hidden="true"
          />
          <input
            type="range"
            className="pod-player__seek"
            min={0}
            max={duration}
            step={1}
            value={currentTime}
            onChange={onSeekInput}
            aria-label="Seek"
          />
        </div>

        <span className="pod-player__time">{formatDuration(duration)}</span>

        <button
          type="button"
          className="pod-player__chip"
          onClick={cycleSpeed}
          aria-label={`Playback speed ${SPEEDS[speedIdx]}x`}
        >
          {SPEEDS[speedIdx]}×
        </button>

        {chapters.length > 0 && (
          <div className="pod-player__chapwrap" ref={chapWrapRef}>
            <button
              type="button"
              className="pod-player__chip"
              onClick={() => setChaptersOpen((o) => !o)}
              aria-expanded={chaptersOpen}
            >
              Chapters
            </button>
            {chaptersOpen && (
              <ul className="pod-player__chapmenu" role="menu">
                {chapters.map((c) => (
                  <li key={c.startTime}>
                    <button
                      type="button"
                      role="menuitem"
                      className={`pod-player__chaprow ${activeChapter && activeChapter.startTime === c.startTime ? 'is-active' : ''}`}
                      onClick={() => jumpChapter(c)}
                    >
                      <span className="pod-player__chapt">{formatDuration(c.startTime)}</span>
                      <span>{c.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="button"
          className="pod-player__chip"
          onClick={copyTimestampLink}
          aria-label="Copy link at current time"
        >
          {copied ? 'Copied' : 'Share at time'}
        </button>
      </div>
    </div>
  );
};

export default PodcastPlayer;
